/**
 * <TextInput> — the maison single/multi-line text field (atom, L0.1).
 *
 * The maison replacement for the paper-based `TextInputM` / `UncontrolledTextInputM`: a controlled RN
 * `TextInput` wrapped in a token-styled bordered field, with an optional label, an error message, a
 * password reveal toggle (reusing the `<IconButton>` atom), and leading/trailing adornment slots.
 * No react-native-paper, no react-hook-form — a plain `value` / `onChangeText` surface so the DS stays
 * isolated from the app.
 *
 * State drives the border color: default / disabled → `border`, focused → `primary`, error → `danger`.
 */
import React, { useState } from "react"
import { Platform, TextInput as RNTextInput, type TextInputProps as RNTextInputProps } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Stack } from "../containers/Stack"
import { Row } from "../containers/Row"
import { ErrorText } from "../typography/ErrorText"
import { Text } from "../typography/Text"
import { IconButton } from "../Buttons/IconButton"

export type TextInputProps = {
    value: string
    onChangeText: (text: string) => void
    placeholder?: string
    /** Label rendered above the field. */
    label?: string
    /** Error message — turns the border red and shows the message below. */
    error?: string
    multiline?: boolean
    numberOfLines?: number
    /** Dims the field and blocks editing. */
    disabled?: boolean
    /**
     * `md` (40, défaut) ou `lg` (44, la hauteur d'un bouton-poussoir) : un champ posé au-dessus d'un
     * bouton, sur un écran de formulaire, s'aligne sur lui.
     */
    size?: TextInputSize
    /** Masks the value and shows an eye toggle (reuses `<IconButton>`). */
    secureTextEntry?: boolean
    /** Leading adornment (e.g. a search `<Icon>`). */
    left?: React.ReactNode
    /** Trailing adornment (e.g. a loading spinner). */
    right?: React.ReactNode
    /** Ce qui se pose à côté de l'intitulé, sur sa ligne (une chip). Rien par défaut. */
    labelAccessory?: React.ReactNode
    /**
     * Le champ natif, pour lui donner le focus depuis un autre geste (« Entrée » sur l'e-mail passe
     * au mot de passe). Rien par défaut. Une prop comme une autre depuis React 19.
     */
    ref?: React.Ref<RNTextInput>
    testID?: string
} & Pick<
    RNTextInputProps,
    | "keyboardType"
    | "autoCapitalize"
    | "autoCorrect"
    | "autoFocus"
    | "returnKeyType"
    | "onSubmitEditing"
    | "onFocus"
    | "onBlur"
    | "maxLength"
    | "textContentType"
    // Ce que le système propose de remplir : l'e-mail, le mot de passe du gestionnaire, le numéro,
    // le code reçu. `textContentType` ne parle qu'à iOS ; Android et le web lisent `autoComplete`.
    | "autoComplete"
    | "accessibilityLabel"
>

/** La hauteur d'un champ d'une ligne. `lg` = la face d'un bouton-poussoir `md`. */
const FIELD_HEIGHT = { md: 40, lg: 44 } as const
type TextInputSize = keyof typeof FIELD_HEIGHT

export function TextInput(props: TextInputProps) {
    const {
        value,
        onChangeText,
        placeholder,
        label,
        error,
        multiline = false,
        numberOfLines,
        disabled = false,
        secureTextEntry = false,
        size = "md",
        left,
        right,
        labelAccessory,
        testID,
        onFocus,
        onBlur,
        ...rest
    } = props

    const { theme } = useUnistyles()
    const [focused, setFocused] = useState(false)
    const [masked, setMasked] = useState(secureTextEntry)

    const borderKey: SemanticColorKey = disabled ? "border" : error != null ? "danger" : focused ? "primary" : "border"
    const bgKey: SemanticColorKey = disabled ? "surface-subtle" : "surface-raised"

    const labelText = label != null && (
        <Text size="text-base" weight="bold" color="primary">
            {label}
        </Text>
    )

    return (
        <Stack gap="1">
            {labelAccessory != null ? (
                <Row gap="2" align="center" style={styles.labelRow}>
                    {labelText}
                    {labelAccessory}
                </Row>
            ) : (
                labelText
            )}

            <Row
                align={multiline ? "stretch" : "center"}
                gap="2"
                style={styles.field(borderKey, bgKey, multiline, disabled, size)}
            >
                {left}
                <RNTextInput
                    testID={testID}
                    style={styles.input(multiline)}
                    value={value}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    placeholderTextColor={theme.colors["text-muted"]}
                    editable={!disabled}
                    multiline={multiline}
                    numberOfLines={numberOfLines}
                    secureTextEntry={masked}
                    onFocus={(e) => {
                        setFocused(true)
                        onFocus?.(e)
                    }}
                    onBlur={(e) => {
                        setFocused(false)
                        onBlur?.(e)
                    }}
                    {...rest}
                />
                {secureTextEntry && (
                    <IconButton
                        iconName={masked ? "eye" : "eye-slash"}
                        appearance="transparent"
                        iconColor="text-muted"
                        size={32}
                        onPress={() => setMasked((m) => !m)}
                        accessibilityLabel={masked ? "Afficher le texte" : "Masquer le texte"}
                    />
                )}
                {right}
            </Row>

            {error != null && <ErrorText>{error}</ErrorText>}
        </Stack>
    )
}

/** La hauteur de la boîte d'un champ d'une ligne : un champ qui lui ressemble, et son squelette, la reprennent. */
export const TEXT_INPUT_HEIGHT = FIELD_HEIGHT.md

const styles = StyleSheet.create((theme) => ({
    field: (
        borderKey: SemanticColorKey,
        bgKey: SemanticColorKey,
        multiline: boolean,
        disabled: boolean,
        size: TextInputSize
    ) => ({
        borderWidth: 1,
        borderColor: theme.colors[borderKey],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[bgKey],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: multiline ? theme.spacing["2"] : 0,
        minHeight: multiline ? 100 : FIELD_HEIGHT[size],
        opacity: disabled ? 0.5 : 1,
    }),
    input: (multiline: boolean) => ({
        flex: 1,
        fontFamily: theme.fontFamilies.regular,
        fontSize: theme.fontSizes["text-base"],
        color: theme.colors["text-strong"],
        paddingVertical: 0,
        textAlignVertical: (multiline ? "top" : "center") as "top" | "center",
        // react-native-web renders a browser focus ring; the field border already signals focus.
        ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : null),
    }),
    /** L'intitulé et ce qui l'accompagne : la chip passe dessous quand la ligne est trop courte. */
    labelRow: { flexWrap: "wrap" },
}))

/**
 * Le cadre d'un champ, pour ce qui se dessine COMME un champ sans en être un (`<CodeInput>`,
 * `<ReadOnlyField>`) : ils le lisent ici au lieu de le recopier chez eux, comme `optionCardContour`
 * pour les cartes d'option. Les mêmes valeurs que `styles.field` ci-dessus, que le champ lui-même
 * garde tel quel : son rendu en prod ne passe pas par cette feuille.
 */
export const fieldContour = StyleSheet.create((theme) => ({
    frame: (borderKey: SemanticColorKey, bgKey: SemanticColorKey, disabled: boolean) => ({
        borderWidth: 1,
        borderColor: theme.colors[borderKey],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[bgKey],
        opacity: disabled ? 0.5 : 1,
    }),
}))
